import { formatDateToddmmyyhhmm } from '../../../utils/dateformatter';

export const isValidDateValue = value => {
  if (value == null || value === '') return false;
  return !Number.isNaN(new Date(value).getTime());
};

export const OPERATIONAL_TOLERANCE_PATTERN = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/;

export const OPERATIONAL_TOLERANCE_OPTIONS = [
  { value: 'Buyer Option', label: 'Buyer Option' },
  { value: 'Seller Option', label: 'Seller Option' },
];

export const OPERATIONAL_TOLERANCE_OPTION_TO_COUNTER = {
  'Buyer Option': 1,
  'Seller Option': 2,
};

export const OPERATIONAL_TOLERANCE_COUNTER_TO_OPTION = {
  1: 'Buyer Option',
  2: 'Seller Option',
};

export const COUNTER_API_FIELD_MAP = {
  operationalTolerance: 'operationalToleranceCounter',
  proposalValidityPeriodMax: 'proposalValidityPeriodMaxCounter',
};

export const getOperationalToleranceOptionsCounter = optionValue =>
  OPERATIONAL_TOLERANCE_OPTION_TO_COUNTER[optionValue] ?? null;

export const resolveOperationalToleranceOption = ({
  label,
  optionsCounter,
} = {}) => {
  if (label != null && String(label).trim() !== '') return String(label).trim();
  if (optionsCounter == null || optionsCounter === '') return null;
  if (OPERATIONAL_TOLERANCE_OPTION_TO_COUNTER[optionsCounter] != null) {
    return optionsCounter;
  }
  return (
    OPERATIONAL_TOLERANCE_COUNTER_TO_OPTION[Number(optionsCounter)] ??
    OPERATIONAL_TOLERANCE_COUNTER_TO_OPTION[optionsCounter] ??
    null
  );
};

export const isValidOperationalToleranceValue = value => {
  if (value == null || value === '') return false;
  return OPERATIONAL_TOLERANCE_PATTERN.test(String(value).trim());
};

export const parseOperationalToleranceValue = value => {
  const normalized = String(value).trim();
  if (!isValidOperationalToleranceValue(normalized)) return null;
  return Number(normalized);
};

export const formatDateTime = dateString => {
  if (!isValidDateValue(dateString)) return '-';
  return formatDateToddmmyyhhmm(dateString) || '-';
};

export const formatOperationalTolerance = value =>
  value != null && value !== '' ? `${value}%` : '-';

export const formatOperationalToleranceCounterDisplay = (tolerance, option) => {
  if (!isValidOperationalToleranceValue(tolerance)) {
    return option || null;
  }
  const toleranceLabel = formatOperationalTolerance(tolerance);
  return option ? `${toleranceLabel} | ${option}` : toleranceLabel;
};

export const hasOperationalToleranceCounter = (
  tolerance,
  optionsCounter,
  optionsCounterLabel
) => {
  if (isValidOperationalToleranceValue(tolerance)) return true;
  if (optionsCounter != null && String(optionsCounter).trim() !== '') {
    return true;
  }
  if (
    optionsCounterLabel != null &&
    String(optionsCounterLabel).trim() !== ''
  ) {
    return true;
  }
  return false;
};

export const hasProposalValidityCounter = value => isValidDateValue(value);

export const toDateTimeLocalValue = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const pad = value => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const getCounterDraftValue = ({ field, form, savedCounters = {} }) => {
  if (field === 'operationalTolerance') {
    const savedValue =
      form.operationalToleranceCounter ?? savedCounters.operationalTolerance;
    return {
      operationalTolerance: savedValue ?? form.operationalTolerance ?? '',
      operationalToleranceOption:
        resolveOperationalToleranceOption({
          label: form.operationalToleranceOptionsCounterLabel,
          optionsCounter: form.operationalToleranceOptionsCounter,
        }) ??
        savedCounters.operationalToleranceOption ??
        '',
    };
  }

  const savedValue =
    form.proposalValidityPeriodMaxCounter ??
    savedCounters.proposalValidityPeriodMax;

  if (isValidDateValue(savedValue)) {
    return { proposalValidityPeriodMax: toDateTimeLocalValue(savedValue) };
  }

  const fallback = savedValue ?? form.proposalValidityPeriodMax;
  return {
    proposalValidityPeriodMax: isValidDateValue(fallback)
      ? toDateTimeLocalValue(fallback)
      : '',
  };
};

export const buildCounterPayload = ({
  field,
  value = null,
  operationalToleranceCounter,
  operationalToleranceOptionsCounter,
  proposalValidityPeriodMaxCounter,
}) => {
  const apiField = COUNTER_API_FIELD_MAP[field];
  if (!apiField) return null;

  return {
    operationalToleranceCounter,
    operationalToleranceOptionsCounter,
    proposalValidityPeriodMaxCounter,
    [apiField]: value,
  };
};
